import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, Warning, ArrowsOut, X as XIcon } from "@phosphor-icons/react";

const fmt = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

/**
 * Centro de avisos global — muestra todas las incidencias/ampliaciones
 * sin responder visibles para el usuario actual. Se refresca cada 45s.
 */
export default function IncidenciasNotifier() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const load = async () => {
    try {
      const { data } = await api.get("/expedientes/incidencias/pending");
      setItems(data.items || []);
    } catch (_e) {
      // silencioso — el aviso es best-effort
    }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 45000);
    return () => clearInterval(timerRef.current);
  }, []);

  // Recargar al cambiar de ruta (tras responder en un expediente, refleja al volver)
  useEffect(() => { load(); }, [location.pathname]);

  const total = items.length;

  const goTo = (expId) => {
    setOpen(false);
    navigate(`/review/${expId}`);
  };

  return (
    <div className="fixed bottom-20 left-4 z-40" data-testid="incidencias-notifier">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="incidencias-bell"
        className={`relative inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-[12px] font-semibold shadow-md transition-colors ${
          total > 0
            ? "border-amber-400/60 bg-amber-50 text-amber-800 hover:bg-amber-100"
            : "border-border bg-white text-muted hover:text-foreground"
        }`}
        title={total > 0 ? `${total} pendiente${total === 1 ? "" : "s"}` : "Sin avisos"}
      >
        <Bell className="w-4 h-4" weight={total > 0 ? "fill" : "bold"} />
        <span className="hidden sm:inline">Avisos</span>
        {total > 0 && (
          <span
            data-testid="incidencias-count"
            className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-600 text-white text-[10px] font-bold"
          >
            {total}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="incidencias-dropdown"
          className="absolute left-0 bottom-full mb-2 w-[380px] max-h-[520px] overflow-y-auto bg-white border border-border rounded-lg shadow-xl"
        >
          <div className="sticky top-0 flex items-center justify-between px-4 py-2.5 border-b border-border bg-white">
            <div className="text-[11px] uppercase tracking-widest font-semibold text-muted">
              Avisos pendientes {total > 0 && <span className="text-amber-700">· {total}</span>}
            </div>
            <button
              onClick={() => setOpen(false)}
              className="p-1 text-muted hover:text-foreground"
              data-testid="incidencias-close"
            >
              <XIcon className="w-3.5 h-3.5" weight="bold" />
            </button>
          </div>

          {total === 0 ? (
            <div className="px-4 py-6 text-center text-[12px] text-muted">
              No hay incidencias ni ampliaciones pendientes de responder.
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {items.map(({ expediente_id, numero_siniestro, aseguradora, item }) => {
                const Icon = item.tipo === "incidencia" ? Warning : ArrowsOut;
                const color = item.tipo === "incidencia" ? "text-rose-600" : "text-amber-600";
                return (
                  <li key={item.id}>
                    <button
                      onClick={() => goTo(expediente_id)}
                      data-testid={`incidencia-item-${item.id}`}
                      className="w-full text-left px-4 py-3 hover:bg-surface transition-colors"
                    >
                      <div className="flex items-start gap-2.5">
                        <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${color}`} weight="bold" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 text-[11px] text-muted uppercase tracking-widest font-semibold">
                            <span>{item.tipo === "incidencia" ? "Incidencia" : "Ampliación"}</span>
                            <span className="text-border">·</span>
                            <span className="normal-case tracking-normal font-medium text-foreground truncate">
                              {aseguradora || "—"} {numero_siniestro ? `· ${numero_siniestro}` : ""}
                            </span>
                          </div>
                          <div className="text-[12px] text-foreground mt-1 line-clamp-2">
                            {item.pregunta}
                          </div>
                          <div className="text-[10px] text-muted mt-1 normal-case tracking-normal">
                            {item.created_by_name || "Gestor"} · {fmt(item.created_at)}
                          </div>
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
